import { useEffect, useState } from 'react';
import { Avatar } from '@telegram-apps/telegram-ui';
import styles from './ProfilePage.module.css';
import { get } from '../../helpers/api';
import { firstChineseChar } from '../../helpers/firstChineseChar';

type UserInfo = {
  id: number;
  username: string;
  realname: string;
  nickname: string;
  mobile: string;
  email: string;
  telegram: string;
  avatar: string;
  intro: string;
};

type Props = {
  onLogout: () => void;
};

const displayName = (user: UserInfo | null) =>
  user?.realname || user?.nickname || user?.username || '未登录';

const getPhotoUrl = (): string | undefined => {
  const tg = (window as unknown as {
    Telegram?: { WebApp?: { initDataUnsafe?: { user?: { photo_url?: string } } } };
  }).Telegram;
  return tg?.WebApp?.initDataUnsafe?.user?.photo_url;
};

type InfoRow = { label: string; value: string };

export const ProfilePage = ({ onLogout }: Props) => {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    get<UserInfo>('/admin/tel/user/info')
      .then((data) => {
        if (!cancelled) {
          setUser(data);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : '加载失败');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const photoUrl = getPhotoUrl();
  const name = displayName(user);

  const rows: InfoRow[] = user
    ? [
        { label: '用户名', value: user.username || '-' },
        { label: '昵称', value: user.nickname || '-' },
        { label: '手机号', value: user.mobile || '-' },
        { label: '邮箱', value: user.email || '-' },
        {
          label: 'Telegram',
          value: user.telegram ? `@${user.telegram.replace(/^@/, '')}` : '-',
        },
      ]
    : [];

  return (
    <div className="card">
      <div className={styles.hero}>
        {photoUrl ? (
          <Avatar size={48} src={photoUrl} alt={name} />
        ) : (
          <span className={styles.avatarPlaceholder}>
            {firstChineseChar(name) || '?'}
          </span>
        )}
        <div className={styles.heroName}>{name}</div>
        {user?.telegram && (
          <div className={styles.heroSub}>@{user.telegram.replace(/^@/, '')}</div>
        )}
      </div>

      {loading ? (
        <div className={styles.loading}>
          <span className="spinner" />
        </div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <div className={styles.infoList}>
          {rows.map((row) => (
            <div key={row.label} className={styles.infoRow}>
              <span className={styles.infoLabel}>{row.label}</span>
              <span className={styles.infoValue}>{row.value}</span>
            </div>
          ))}
        </div>
      )}

      <div className={styles.actions}>
        <button type="button" className={styles.logout} onClick={onLogout}>
          退出登录
        </button>
      </div>
    </div>
  );
};
